import { BillingPeriodUtils } from '../utils/billingPeriod';
import { PatientService } from './PatientService';
import {
  RenewalDecision,
  RenewalDecisionOption,
  RenewalStatus,
  UnifiedPatient
} from '../types/unified-financial';

export type RenewalDecisionAction = RenewalDecisionOption;

export interface RenewalDecisionTask {
  patient: UnifiedPatient;
  decision: RenewalDecision;
}

export interface ResolveDecisionPayload {
  patientId: string;
  decisionId: string;
  action: RenewalDecisionAction;
  clientId?: string;
  notes?: string;
  decidedBy?: string;
  obitoDate?: string;
}

const createDecisionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `rd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

const normalizeDateInput = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const direct = Date.parse(trimmed);
  if (!Number.isNaN(direct)) {
    const parsed = new Date(direct);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }

  const brazilian = trimmed.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (brazilian) {
    const [, dayStr, monthStr, yearStr] = brazilian;
    const day = parseInt(dayStr, 10);
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);
    if (!Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year)) {
      const candidate = new Date(year, month - 1, day, 0, 0, 0, 0);
      if (!Number.isNaN(candidate.getTime())) {
        return candidate.toISOString();
      }
    }
  }

  const isoDate = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDate) {
    const [, yearStr, monthStr, dayStr] = isoDate;
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);
    if (!Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year)) {
      const candidate = new Date(year, month - 1, day, 0, 0, 0, 0);
      if (!Number.isNaN(candidate.getTime())) {
        return candidate.toISOString();
      }
    }
  }

  console.warn(`[RenewalDecisionService] Não foi possível interpretar data "${value}"`);
  return undefined;
};

const toComparableDate = (iso?: string): string | undefined => {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
};

const mapActionToStatus = (action: RenewalDecisionAction): RenewalStatus => {
  switch (action) {
    case 'renew':
      return 'active';
    case 'individual_sessions':
      return 'active';
    case 'discharge':
      return 'inactive';
    case 'deceased':
      return 'deceased';
    default:
      return 'active';
  }
};

export class RenewalDecisionService {
  /**
   * Garante que pacientes com pacote ativo possuam decisão de renovação pendente
   * para o ciclo atual.
   */
  static async ensurePendingDecisions(
    patients: UnifiedPatient[],
    month: string
  ): Promise<UnifiedPatient[]> {
    const result: UnifiedPatient[] = [];

    for (const patient of patients) {
      const financialProfile = patient.financialProfile;
      if (!financialProfile) {
        result.push(patient);
        continue;
      }

      const {
        hasPackage,
        packageInfo,
        renewalDecisions = [],
        renewalStatus
      } = financialProfile;

      const skipStatuses: RenewalStatus[] = ['inactive', 'deceased'];
      const shouldSkip =
        !hasPackage ||
        !packageInfo ||
        skipStatuses.includes(renewalStatus || 'active');

      if (shouldSkip) {
        result.push(patient);
        continue;
      }

      const hasPending = renewalDecisions.some(decision => decision.status === 'pending');
      let updatedPatient = patient;

      if (!hasPending) {
        try {
          const { startDate, endDate } = BillingPeriodUtils.calculateBillingPeriod(patient, month);
          const periodStartIso = startDate.toISOString();
          const periodEndIso = endDate.toISOString();

          const alreadyExistsForPeriod = renewalDecisions.some(decision => {
            const startComparable = toComparableDate(decision.periodStart);
            const endComparable = toComparableDate(decision.periodEnd);
            return (
              startComparable === periodStartIso.slice(0, 10) &&
              endComparable === periodEndIso.slice(0, 10)
            );
          });

          if (!alreadyExistsForPeriod) {
            const snapshot =
              packageInfo && typeof packageInfo === 'object'
                ? {
                    packageName: packageInfo.packageName,
                    price: packageInfo.price,
                    sessionsContracted: packageInfo.sessionsContracted,
                    sessionsUsed:
                      packageInfo.sessionsUsed ??
                      financialProfile.package?.sessionsUsed ??
                      financialProfile.currentCycle?.sessionsUsed,
                    sessionsRemaining:
                      packageInfo.sessionsRemaining ??
                      financialProfile.package?.sessionsRemaining ??
                      financialProfile.currentCycle?.sessionsRemaining
                  }
                : undefined;

            const newDecision: RenewalDecision = {
              decisionId: createDecisionId(),
              status: 'pending',
              periodStart: periodStartIso,
              periodEnd: periodEndIso,
              createdAt: new Date().toISOString(),
              packageSnapshot: snapshot
            };

            const updatedDecisions = [...renewalDecisions, newDecision];
            const tenantClientId = financialProfile.clientId;

            await PatientService.updatePatientRenewalData(patient.id, {
              renewalDecisions: updatedDecisions,
              renewalStatus: 'pending'
            }, tenantClientId);

            updatedPatient = {
              ...patient,
              financialProfile: {
                ...financialProfile,
                renewalStatus: 'pending',
                renewalDecisions: updatedDecisions
              }
            };
          }
        } catch (error) {
          console.error(
            `[RenewalDecisionService] Falha ao criar decisão de renovação para paciente ${patient.id}`,
            error
          );
        }
      }

      result.push(updatedPatient);
    }

    return result;
  }

  /**
   * Agrupa decisões pendentes em uma fila para exibição no painel.
   */
  static collectPendingDecisions(patients: UnifiedPatient[]): RenewalDecisionTask[] {
    const tasks: RenewalDecisionTask[] = [];

    for (const patient of patients) {
      const decisions = patient.financialProfile?.renewalDecisions ?? [];
      for (const decision of decisions) {
        if (decision.status === 'pending') {
          tasks.push({ patient, decision });
        }
      }
    }

    return tasks.sort((a, b) => {
      const aTime = Date.parse(a.decision.createdAt ?? '') || 0;
      const bTime = Date.parse(b.decision.createdAt ?? '') || 0;
      return aTime - bTime;
    });
  }

  /**
   * Registra a decisão tomada pelo time administrativo e sincroniza com o Firestore.
   */
  static async resolveDecision(payload: ResolveDecisionPayload): Promise<void> {
    const { patientId, decisionId, action, clientId, decidedBy, notes, obitoDate } = payload;

    const patient = await PatientService.getPatientById(patientId, clientId);
    if (!patient) {
      throw new Error('Paciente não encontrado');
    }

    const decisions = [...(patient.financialProfile.renewalDecisions ?? [])];
    const decisionIndex = decisions.findIndex(item => item.decisionId === decisionId);
    if (decisionIndex === -1) {
      throw new Error('Decisão de renovação não encontrada');
    }

    const originalDecision = decisions[decisionIndex];
    const resolvedAt = new Date().toISOString();

    const resolvedDecision: RenewalDecision = {
      ...originalDecision,
      status: 'resolved',
      resolvedAt,
      decision: action,
      decidedBy: decidedBy ?? 'admin_dashboard',
      notes: notes ?? originalDecision.notes
    };

    if (action === 'deceased') {
      const normalized = normalizeDateInput(obitoDate ?? originalDecision.obitoDate);
      if (normalized) {
        resolvedDecision.obitoDate = normalized;
      }
    } else if (obitoDate !== undefined) {
      const normalized = normalizeDateInput(obitoDate);
      if (normalized) {
        resolvedDecision.obitoDate = normalized;
      }
    }

    decisions[decisionIndex] = resolvedDecision;

    const updateData: Parameters<typeof PatientService.updatePatientRenewalData>[1] = {
      renewalDecisions: decisions,
      renewalStatus: mapActionToStatus(action)
    };

    if (action === 'deceased') {
      const normalized = normalizeDateInput(obitoDate ?? originalDecision.obitoDate);
      if (normalized) {
        updateData.obitoDate = normalized;
      }
    } else if (obitoDate !== undefined) {
      const normalized = normalizeDateInput(obitoDate);
      updateData.obitoDate = normalized ?? null;
    }

    await PatientService.updatePatientRenewalData(
      patientId,
      updateData,
      clientId || patient.financialProfile.clientId
    );
  }
}

